import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BackdropOverlay from "../common/BackdropOverlay.jsx";
import { colors } from "../../theme/colors";
import { DIALOG_PAPER_RADIUS } from "../common/dialogActionButtonSx";
import {
  SMALLER_MATCH_INTRO_LEAD,
  SMALLER_MATCH_INTRO_POINTS,
  SMALLER_MATCH_INTRO_TITLE,
} from "./smallerMatchIntro";

const MotionBox = motion(Box);
const DIALOG_Z_INDEX = 1400;

function formatPointIndex(index) {
  return String(index + 1).padStart(2, "0");
}

export default function SmallerMatchIntroDialog({ open, onClose }) {
  if (!open) {
    return null;
  }

  return (
    <BackdropOverlay
      open={open}
      onClick={onClose}
      zIndex={DIALOG_Z_INDEX}
      sx={{
        inset: 0,
        width: "100%",
        height: "100dvh",
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 2, sm: 3 },
        boxSizing: "border-box",
      }}
    >
      <MotionBox
        role="dialog"
        aria-labelledby="smaller-match-intro-title"
        aria-modal="true"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 560,
          maxWidth: "92vw",
          maxHeight: "calc(100dvh - 48px)",
          borderRadius: DIALOG_PAPER_RADIUS,
          overflow: "hidden",
          border: `1px solid ${alpha(colors.primary.main, 0.18)}`,
          background: `
            linear-gradient(
              180deg,
              rgba(28,27,26,.98),
              rgba(18,18,19,.98)
            )
          `,
          boxShadow: `
            0 30px 80px rgba(0,0,0,.55),
            inset 0 1px 0 rgba(255,255,255,.03)
          `,
        }}
      >
        <Box
          sx={{
            position: "relative",
            flexShrink: 0,
            pt: { xs: "22px", sm: "28px" },
            pb: { xs: "16px", sm: "20px" },
            px: { xs: "20px", sm: "28px" },
            background: `
              linear-gradient(
                180deg,
                rgba(232,163,61,.09),
                transparent
              )
            `,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background: `
                linear-gradient(
                  90deg,
                  transparent,
                  rgba(232,163,61,.45),
                  transparent
                )
              `,
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                background: `
                  linear-gradient(
                    180deg,
                    rgba(232,163,61,.18),
                    rgba(232,163,61,.06)
                  )
                `,
                border: "1px solid rgba(232,163,61,.25)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,.05)",
              }}
            >
              <GroupsOutlinedIcon
                sx={{
                  color: colors.primary.main,
                  fontSize: "1.4rem",
                }}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: colors.primary.main,
                  fontWeight: 700,
                  mb: "0.4rem",
                }}
              >
                What&apos;s new
              </Typography>
              <Typography
                id="smaller-match-intro-title"
                component="h2"
                sx={{
                  fontSize: { xs: "1.35rem", sm: "1.55rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  color: colors.text.primary,
                  lineHeight: 1.15,
                }}
              >
                {SMALLER_MATCH_INTRO_TITLE}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: "20px", sm: "28px" },
            pb: { xs: "8px", sm: "10px" },
            overflowY: "auto",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.95rem",
              lineHeight: 1.65,
              color: colors.text.secondary,
              mb: "18px",
            }}
          >
            {SMALLER_MATCH_INTRO_LEAD}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {SMALLER_MATCH_INTRO_POINTS.map((point, index) => (
              <Box
                key={point.title}
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start",
                  p: "14px",
                  borderRadius: "12px",
                  background: "rgba(255,255,255,.015)",
                  border: "1px solid rgba(255,255,255,.05)",
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    background: "rgba(232,163,61,.12)",
                    border: "1px solid rgba(232,163,61,.18)",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 700,
                    fontSize: "10px",
                    color: colors.primary.main,
                    lineHeight: 1,
                  }}
                >
                  {formatPointIndex(index)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontWeight: 700,
                      fontSize: "12px",
                      color: colors.text.primary,
                      mb: "0.25rem",
                    }}
                  >
                    {point.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.84rem",
                      lineHeight: 1.55,
                      color: "rgba(255,255,255,.72)",
                    }}
                  >
                    {point.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: { xs: "20px", sm: "28px" },
            py: { xs: "16px", sm: "20px" },
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="button"
            onClick={onClose}
            sx={{
              height: "42px",
              px: "22px",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              color: colors.text.contrast,
              background: `
                linear-gradient(
                  180deg,
                  #F1B24B,
                  #D89128
                )
              `,
              border: "1px solid rgba(255,255,255,.08)",
              boxShadow: `
                0 10px 20px rgba(232,163,61,.15),
                inset 0 1px 0 rgba(255,255,255,.15)
              `,
              transition: "filter 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
              "&:hover": {
                filter: "brightness(1.04)",
                background: `
                  linear-gradient(
                    180deg,
                    #F1B24B,
                    #D89128
                  )
                `,
                boxShadow: `
                  0 10px 20px rgba(232,163,61,.15),
                  inset 0 1px 0 rgba(255,255,255,.15)
                `,
              },
            }}
          >
            Got it
          </Button>
        </Box>
      </MotionBox>
    </BackdropOverlay>
  );
}

SmallerMatchIntroDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
